import { kalmanFilter } from "../support/kalman";
import { derived, writable, type Writable } from "svelte/store";
import { logError, ERRORS, type ErrorMessage } from "./errors";
import { accumulate } from "../support/observable";
import { seconds } from "../support/dates";
import { get } from "lodash-es";
import type { Location } from "../types";

type GeoError = {
  code: number;
};

export const FAILED = 0;
export const INACTIVE = 1;
export const LOADING = 2;
export const ACTIVE = 3;

const MAX_WAIT = 3500;

// Never use a value more than 10 seconds old
const MAX_AGE = seconds(10);

/* https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions */
const gpsOptions = {
  // Should we receive the best possible result?
  enableHighAccuracy: true,
  // How long are we allowed to wait in order to get an accurate position?
  timeout: 10000,
  // How long is a valid location cache good for?
  // Setting this to zero ensures no value is cached
  maximumAge: 0
};

// Minimum number of attempts to locate before using data. Helps
// with "warming" GPS
const REQUIRED_ATTEMPTS = 3;

// Maximum number of samples of prior geolocation should we use for smoothing?
const SMOOTHING_SAMPLE = 5;

export const gpsStatus = writable(INACTIVE);

function reportError(error: GeoError) {
  gpsStatus.set(FAILED);
  logError(translateError(error));
}

function reportUnsupportedError() {
  gpsStatus.set(FAILED);
  reportError({ code: 0 });
}

export const lastReading: Writable<Location> = writable();
export const priorReadings = accumulate<Location>(lastReading, SMOOTHING_SAMPLE);

export const geolocation = derived(
  [lastReading, priorReadings],
  ([$lastReading, $priorReadings], set) => {
    const measurements = $priorReadings.filter(Boolean);
    const youngestAge = get($lastReading, "timestamp", Date.now());
    const samples = measurements.filter((item: Location) => youngestAge - item.timestamp < MAX_AGE);

    if (samples.length > 0) {
      set(kalmanFilter(samples));
    }
  }
);

type WatchID = number;

type GeolocationAdapter = {
  isSupported: boolean;
  watch: (
    success: PositionCallback,
    error: PositionErrorCallback,
    options: PositionOptions
  ) => WatchID;
  clear: (watchId: WatchID | null) => void;
};

const buildBrowserGPS = function () {
  const adapter: GeolocationAdapter = {
    isSupported: navigator.geolocation != null,
    watch(success: PositionCallback, error: PositionErrorCallback, options: PositionOptions) {
      return navigator.geolocation.watchPosition(success, error, options);
    },
    clear(watchId: WatchID | null) {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    }
  };

  return adapter;
};

let adapter: GeolocationAdapter | null = null;
let watchId: WatchID | null = null;

export function startGps(adapterBuilder = buildBrowserGPS) {
  if (adapter) {
    return;
  }

  adapter = adapterBuilder();

  gpsStatus.set(LOADING);

  let attempts = 0;
  let lastLocation: Location;

  function emit() {
    if (lastLocation) {
      gpsStatus.set(ACTIVE);
      lastReading.set(lastLocation);
    }
  }

  if (adapter.isSupported) {
    setTimeout(function () {
      if (lastLocation) {
        emit();
      }
    }, MAX_WAIT);

    // Initialize early to avoid "Cannot access uninitialized variable"
    watchId = adapter.watch(
      (position: GeolocationPosition) => {
        lastLocation = {
          accuracy: position.coords.accuracy,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          timestamp: position.timestamp
        };

        attempts += 1;

        /* Skip the first few attempts, which are usually garbage */
        if (attempts >= REQUIRED_ATTEMPTS) {
          emit();
        }
      },
      (error: GeolocationPositionError) => {
        if (adapter) {
          adapter.clear(watchId);
          adapter = null;
        }

        reportError(error);
      },
      gpsOptions
    );
  } else {
    reportUnsupportedError();
  }
}

export function stopGps() {
  if (adapter) {
    adapter.clear(watchId);
  }

  adapter = null;

  gpsStatus.set(INACTIVE);
}

/**
 * Translate GPS errors into friendly messages
 * https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError/code
 */

const messages: { [key: number | string]: ErrorMessage } = {
  0: ERRORS.geolocation.unsupported,
  1: ERRORS.geolocation.permission_denied,
  2: ERRORS.geolocation.unavailable,
  3: ERRORS.geolocation.timeout,
  fallback: ERRORS.geolocation.general
};

function translateError(error: GeoError): ErrorMessage {
  return messages[error.code] || messages.fallback;
}
